import { observer } from "mobx-react";
import { useContext, useEffect, useState, useCallback } from "react";
import { useHistory, useLocation } from "react-router"; // Added useLocation

import { LoginFormComponent } from "@kimoycatayas/pe-ui-kit-test";
import { UserStore } from "../..";
import ApiClient from "../../ApiClient";
import { PEProgressBar } from "../linear-progress";
import { PageContainer } from "../ui/PageComponents";

// Define interfaces for better type safety
interface LoginResponse {
  data: {
    user: any; // Replace with actual user type
    commandCentreVenues: any[]; // Replace with actual venue type
  };
}

const LoginPage = observer(() => {
  const userStore = useContext(UserStore);
  const history = useHistory();
  const location = useLocation(); // Added to get query params

  // Get redirect param from URL
  const searchParams = new URLSearchParams(location.search);
  const redirectTo = searchParams.get("redirect") || "/home";
  const venueId = searchParams.get("venueId") || "";
  const modal = searchParams.get("modal") || "";
  const [loginInProgress, setLoginInProgress] = useState(false);

  async function handleLoginResponse(email: string, password: string) {
    try {
      setLoginInProgress(true);

      const response = (await ApiClient.userLogin(
        email,
        password
      )) as LoginResponse;

      userStore.login(response.data.user, response.data.commandCentreVenues);
      // Pass the redirect param to select-venue

      history.push(
        `/select-venue?redirect=${encodeURIComponent(redirectTo)}${venueId ? `&v=${venueId}` : ""}${modal ? `&modal=${modal}` : ""}`
      );
    } catch (err) {
      console.error("Login error:", err);
    } finally {
      setLoginInProgress(false);
    }
  }

  // Memoize handleSubmit to prevent unnecessary re-renders
  const handleSubmit = async (email: string, password: string) => {
    if (document.requestStorageAccess) {
      document
        .requestStorageAccess()
        .then(async () => {
          console.log("Storage access granted");
          // Now make your fetch/XHR call; cookies should work
          handleLoginResponse(email, password);
        })
        .catch(() => {
          console.log("Storage access denied");
          // Fallback: Prompt user to sign in manually or use token auth
        });
    } else {
      handleLoginResponse(email, password);
    }
  };

  // Handle redirection logic
  useEffect(() => {
    const { isLoggedIn, selectedVenue } = userStore;

    if (isLoggedIn && !selectedVenue) {
      history.push(
        `/select-venue?redirect=${encodeURIComponent(redirectTo)}${venueId ? `&v=${venueId}` : ""}
        ${modal ? `&modal=${modal}` : ""}`
      );
    }

    if (isLoggedIn && selectedVenue) {
      // If there's a redirect param and venue is selected, go there
      // Otherwise go to home

      const uri = decodeURIComponent(
        redirectTo + (modal ? `&modal=${modal}` : "") || "/home"
      );

      history.push(uri);
      return;
    }
  }, [userStore, history, redirectTo]); // Added redirectTo to dependencies

  // Loading state
  const renderLoading = () => (
    <div
      className="loading-container"
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <PEProgressBar />
    </div>
  );

  // Login form
  const renderLoginForm = () => (
    <LoginFormComponent
      handleSubmit={handleSubmit}
      title="OK2PLAY"
      subtitle="Command Centre"
      color="white"
      backgroundColor="#E24A3B"
      imageUrl="https://ok2p-test-bucket.s3.ap-southeast-2.amazonaws.com/ok2play_logo_dark.png"
    />
  );

  // No venues state
  const renderNoVenues = () => (
    <PageContainer>
      <div
        style={{
          textAlign: "center",
          fontSize: 16,
          marginBottom: 4,
        }}
      >
        No Venues found
      </div>
    </PageContainer>
  );

  const { isLoggedIn, userVenuesAlphabetical } = userStore;

  if (loginInProgress) {
    return renderLoading();
  }

  if (!isLoggedIn) {
    return renderLoginForm();
  }

  if (userVenuesAlphabetical?.length === 0) {
    return renderNoVenues();
  }

  return renderLoading();
});

export default LoginPage;
